import { useEffect, useState, useSyncExternalStore, type RefObject } from 'react'
import { getOpenSheetCount, subscribeOpenSheets } from '@/components/ui/BottomSheet'

/**
 * When the phone "+" has to get out of the way (UX-111 / WCAG 2.4.11 Focus
 * Not Obscured). It floats bottom-right over page content, so it:
 *
 * - steps aside while someone is typing (a text field has focus, or the
 *   on-screen keyboard has shrunk the visual viewport): the keyboard pushes
 *   the "+" up over the very field being typed in;
 * - steps aside while a bottom sheet or dialog is open: the sheet's own
 *   footer buttons sit exactly where the "+" is;
 * - steps aside while a control that has KEYBOARD focus is under it (a
 *   tap-focused button doesn't count, so tapping near it never makes it
 *   vanish).
 *
 * The "+" is hidden in place (not unmounted) so the space it reserves via
 * --dock-h doesn't change and the page never jumps.
 */

const NON_TEXT_INPUTS = new Set([
  'button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit',
])

export function isTextEntry(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  if (el.isContentEditable) return true
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true
  if (el instanceof HTMLInputElement) return !NON_TEXT_INPUTS.has(el.type)
  return false
}

/** Rects overlap, with `slop` px of breathing room around `b`. */
export function rectsOverlap(a: DOMRect, b: DOMRect, slop = 8): boolean {
  return a.right > b.left - slop && a.left < b.right + slop && a.bottom > b.top - slop && a.top < b.bottom + slop
}

function isKeyboardFocus(el: Element): boolean {
  try {
    return el.matches(':focus-visible')
  } catch {
    return false
  }
}

/** On-screen keyboard: the visual viewport is much shorter than the layout one. */
const KEYBOARD_MIN_PX = 120

export function useFabClearance(fabRef: RefObject<HTMLElement | null>): boolean {
  const sheetsOpen = useSyncExternalStore(subscribeOpenSheets, getOpenSheetCount, () => 0) > 0
  const [typing, setTyping] = useState(false)
  const [keyboard, setKeyboard] = useState(false)
  const [covered, setCovered] = useState(false)

  useEffect(() => {
    let raf = 0
    const check = () => {
      raf = 0
      const fab = fabRef.current
      const active = document.activeElement
      // The "+" itself and its open menu (portaled) never hide it.
      const inFab =
        (!!fab && active instanceof Node && fab.contains(active)) ||
        (active instanceof HTMLElement && !!active.closest('[data-create-menu-layer]'))
      setTyping(!inFab && isTextEntry(active))
      if (!fab || inFab || !(active instanceof HTMLElement) || active === document.body || !isKeyboardFocus(active)) {
        setCovered(false)
        return
      }
      const a = active.getBoundingClientRect()
      const f = fab.getBoundingClientRect()
      // Something with no box (hidden, detached) can't be covered.
      setCovered(a.width > 0 && a.height > 0 && f.width > 0 && rectsOverlap(a, f))
    }
    // Focus moves (and the scroll the browser does to reveal it) settle a
    // frame later; measure then.
    const schedule = () => { if (!raf) raf = requestAnimationFrame(check) }
    const onFocusOut = () => { window.setTimeout(schedule, 0) }
    document.addEventListener('focusin', schedule)
    document.addEventListener('focusout', onFocusOut)
    document.addEventListener('scroll', schedule, { capture: true, passive: true })

    const vv = window.visualViewport
    const onViewport = () => {
      if (!vv) return
      setKeyboard(window.innerHeight - vv.height > KEYBOARD_MIN_PX)
    }
    vv?.addEventListener('resize', onViewport)
    onViewport()
    check()
    return () => {
      if (raf) cancelAnimationFrame(raf)
      document.removeEventListener('focusin', schedule)
      document.removeEventListener('focusout', onFocusOut)
      document.removeEventListener('scroll', schedule, { capture: true } as EventListenerOptions)
      vv?.removeEventListener('resize', onViewport)
    }
  }, [fabRef])

  return sheetsOpen || typing || keyboard || covered
}
